import React from 'react';
import { connect } from 'react-redux';
import { MuiThemeProvider, createTheme } from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import { resultsConfigSelector, quoteFormSelector } from 'ctm-selectors';
import { withRules, SimpleFlexbox, ModalCloseIcon } from 'ctm-components';
import moment from 'moment';
import { onReplaceLinkClick, pageContext } from 'ctm-util-functions';
import { clearQuoteModelRedirect } from 'ctm-components/components/results/actions';
import styled from 'styled-components';

const theme = createTheme({
    overrides: {
        MuiPaper: {
            root: {
                padding: '2rem',
                // To fix the absolute-positioned close icon being cut off
                overflowY: 'unset !important'
            }
        }
    }
});

const Title = styled.h1`
    font-size: 1.6rem;
    color: var(--brand-primary);
    font-weight: 600;
    margin-bottom: 0.5rem;
    line-height: 20px;
`;

const ButtonContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    gap: 1rem;
    margin-top: 1rem;
`;

const Text = styled.p`
    font-size: 1.4rem;
    line-height: 1.7rem;
    color: var(--brand-base);
    font-weight: 400;
    margin-bottom: 0.5rem;
`;

const Button = styled.button`
    background: var(--brand-base-0);
    border: ${(props) => props.border};
    box-sizing: border-box;
    border-radius: 4px;
    font-size: 1.4rem;
    font-weight: 500;
    color: var(--brand-primary);
    padding: 14px 24px;
    height: 4.4rem;
    &:hover {
        cursor: pointer;
    }
    display: flex;
    align-items: center;
    justify-content: center;
    width: 12rem;
`;

const _UpdateQuoteModal = (props) => {
    const { UpdateQuoteModalIsOpen, closeModal, commencementDate, quoteModalRedirect, resetQuoteModelRedirect, editDetailsRedirect, resetHasSavedResults, fetchNewResults } = props;
    const renderCloseIcon = () => {
        const closeIconProps = { onClickFunction: handleCancel };
        return React.createElement(ModalCloseIcon, closeIconProps);
    };

    const handleCancel = () => {
        closeModal();
        resetQuoteModelRedirect();
    };

    const handleRedirect = () => {
        const browserHistory = pageContext.getProps().browserHistory;
        const currentDate = moment().format('YYYY-MM-DD');
        const isCommencementDateTodayOrFuture = !!commencementDate && moment(currentDate).isSameOrBefore(commencementDate);

        closeModal();
        if (quoteModalRedirect) {
            editDetailsRedirect();
            browserHistory.push(quoteModalRedirect);
        } else if (!isCommencementDateTodayOrFuture) {
            resetHasSavedResults();
            browserHistory.push('/car-insurance/journey/commencement_date');
        } else {
            fetchNewResults();
        }
    };

    return (
        <MuiThemeProvider theme={theme}>
            <Dialog fullwidth open={UpdateQuoteModalIsOpen} disableBackdropClick disableEscapeKeyDown>
                <SimpleFlexbox display="flex" column>
                    <Title>Update quote details</Title>
                    <Text>This will replace your current quotes and prices may change.</Text>
                    <Text>You may be asked to update your policy start date if it is now in the past.</Text>
                </SimpleFlexbox>
                {renderCloseIcon()}
                <ButtonContainer>
                    <Button
                        id="savedResults.ModalDialogueButton.cancel"
                        onClick={() => {
                            handleCancel();
                        }}
                        type="button"
                    >
                        Cancel
                    </Button>
                    <Button
                        id="savedResults.ModalDialogueButton.update"
                        type="button"
                        border={'1px solid var(--brand-primary)'}
                        onClick={() => {
                            handleRedirect();
                        }}
                    >
                        Update
                    </Button>
                </ButtonContainer>
            </Dialog>
        </MuiThemeProvider>
    );
};

const mapDispatchToProps = (dispatch) => ({

    resetHasSavedResults: () => {
        dispatch({
            type: 'SET_RESULTS_CONFIG_VALUE',
            key: 'helpers.userHasSavedResults',
            value: false
        });
        dispatch({
            type: 'SET_RESULTS_CONFIG_VALUE',
            key: 'filters.fetchNewResults',
            value: true
        });
    },

    fetchNewResults: () => {
        dispatch({
            type: 'SET_RESULTS_CONFIG_VALUE',
            key: 'helpers.userHasSavedResults',
            value: false
        });
        dispatch({
            type: 'SET_RESULTS_CONFIG_VALUE',
            key: 'filters.fetchNewResults',
            value: true
        });
        dispatch({
            type: 'SET_PAGE_VALUE',
            identifier: 'lastViewedPage',
            value: 'results'
        });
        dispatch({
            type: 'SET_RESULTS_CONFIG_VALUE',
            key: 'helpers.fetchedNewResults',
            value: true
        });
        dispatch({
            type: 'ACTIVATE_STAGED_FILTERS'
        });
        dispatch({
            type: 'SET_PAGE_VALUE',
            identifier: 'doReload',
            value: true
        });
    },

    editDetailsRedirect: () => {
        dispatch({
            type: 'SET_RESULTS_CONFIG_VALUE',
            key: 'helpers.fetchedNewResults',
            value: true
        });
        dispatch({
            type: 'SET_RESULTS_CONFIG_VALUE',
            key: 'helpers.userHasSavedResults',
            value: false
        });
        dispatch({
            type: 'SET_PAGE_VALUE',
            identifier: 'hasSeenResults',
            value: true
        });
    },

    resetQuoteModelRedirect: () => {
        dispatch(clearQuoteModelRedirect());
    }
});

const mapStateToProps = (state) => {
    const isModalOpen = resultsConfigSelector(state).getIn(['helpers', 'updateQuoteModal']);
    const commencementDate = quoteFormSelector(state).getIn(['coverDetail', 'commencementDate']);
    const quoteModalRedirect = resultsConfigSelector(state).getIn(['helpers', 'quoteModalRedirect']);

    return { UpdateQuoteModalIsOpen: isModalOpen ? isModalOpen.open : false, commencementDate, quoteModalRedirect };
};

const UpdateQuoteModal = withRules(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )(_UpdateQuoteModal)
);

export { _UpdateQuoteModal, UpdateQuoteModal };
